<template>
    <div id="editprofilepage">
        <div class="top-buttons">
            <ui5-button @click="saveEditProfile">
                {{ trans('pages.editprofile.save') }}
            </ui5-button>
            <ui5-button @click="$router.go(-1)">
                {{ trans('pages.editprofile.cancel') }}
            </ui5-button>
        </div>

        <pre
            class="debug"
            v-if="isDebug"
        >
      {{ uploadType }}
      {{ $v }}
      {{ profile }}
      {{ isValid() ? 'valid' : 'invalid' }}
    </pre
        >

        <spinner-text v-if="uploading">
            {{ trans('pages.editprofile.uploading') }}
        </spinner-text>

        <spinner-text v-if="loading">
            {{ trans('pages.editprofile.loading') }}
        </spinner-text>
        <template v-else-if="profile">
            <div class="field">
                <ui5-label
                    for="description"
                    required
                >
                    {{ trans('pages.editprofile.description_nl') }}
                    {{ getEmoji('nl-BE') }}
                </ui5-label>
                <ui5-input
                    type="Text"
                    name="description"
                    id="description"
                    :value="profile.description"
                    @input="profile.description = $event.target.value"
                    required
                />
                <div
                    class="error"
                    v-if="getErrorFromValidator($v.profile.description)"
                >
                    {{ getErrorFromValidator($v.profile.description) }}
                </div>
            </div>
            <div class="field">
                <ui5-label for="descriptionFR">
                    {{ trans('pages.editprofile.description_fr') }}
                    {{ getEmoji('fr-BE') }}
                </ui5-label>
                <ui5-input
                    type="Text"
                    name="descriptionFR"
                    id="descriptionFR"
                    :value="profile.descriptionFR"
                    @input="profile.descriptionFR = $event.target.value"
                />
                <div
                    class="error"
                    v-if="getErrorFromValidator($v.profile.descriptionFR)"
                >
                    {{ getErrorFromValidator($v.profile.descriptionFR) }}
                </div>
            </div>

            <div class="field">
                <ui5-label
                    for="code"
                    required
                >
                    {{ trans('pages.editprofile.article_number') }}
                </ui5-label>
                <ui5-input
                    type="text"
                    name="code"
                    id="code"
                    :value="profile.code"
                    @input="profile.code = $event.target.value"
                    @change="profileCodeChanged"
                    required
                />
                <div
                    class="error"
                    v-if="codeExists"
                >
                    {{ trans('pages.editprofile.article_number_exists') }}
                </div>
                <div
                    class="error"
                    v-if="
                        profile.profileId &&
                        getErrorFromValidator($v.profile.code)
                    "
                >
                    {{ getErrorFromValidator($v.profile.code) }}
                </div>
                <template v-if="!profile.profileId">
                    <spinner v-if="fetchingProfiles" />
                </template>
            </div>
            <div
                class="field"
                v-if="!profile.profileId"
            >
                <ui5-label for="code">&nbsp;</ui5-label>
                <ui5-select
                    @change="
                        selectedPrefixes[0] =
                            $event.detail.selectedOption.value;
                        fillPrefixSelection();
                    "
                >
                    <ui5-option
                        v-for="prefix in levelOneOptions"
                        :key="'level.1.' + prefix.ID"
                        :selected="prefix.ID == selectedPrefixes[0]"
                        :value="prefix.ID"
                    >
                        <template v-if="isDebug">#{{ prefix.ID }} -</template>
                        {{ prefix.label }}
                    </ui5-option>
                </ui5-select>
                <ui5-select
                    @change="
                        selectedPrefixes[1] =
                            $event.detail.selectedOption.value;
                        fillPrefixSelection();
                    "
                >
                    <ui5-option
                        v-for="prefix in levelTwoOptions"
                        :key="'level.2.' + prefix.ID"
                        :selected="prefix.ID == selectedPrefixes[1]"
                        :value="prefix.ID"
                    >
                        <template v-if="isDebug">#{{ prefix.ID }} -</template>
                        {{ prefix.label }}
                    </ui5-option>
                </ui5-select>
                <ui5-select
                    @change="
                        selectedPrefixes[2] =
                            $event.detail.selectedOption.value;
                        fillPrefixSelection();
                    "
                >
                    <ui5-option
                        v-for="prefix in levelThreeOptions"
                        :key="'level.3.' + prefix.ID"
                        :selected="prefix.ID == selectedPrefixes[2]"
                        :value="prefix.ID"
                    >
                        <template v-if="isDebug">#{{ prefix.ID }} -</template>
                        {{ prefix.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div v-if="profile">
                <drop-zone
                    v-if="profile.profileId"
                    v-model="imageFiles"
                    :upload-files="uploadFiles"
                    :file-removed="fileRemoved"
                    :main-image-set="mainImageSet"
                    :mimes="imageFileTypes"
                />
                <form id="edit-profile">
                    <h2>{{ trans('pages.editprofile.general_info') }}</h2>
                    <template v-if="profile.profileId">
                        <input
                            type="file"
                            ref="inputFileUpload"
                            data-type="paint-image"
                            @change="inputFileUploadChange"
                            style="display: none"
                        />
                        <div
                            class="field"
                            id="editlakfoto"
                        >
                            <ui5-label for="lakfotoimg">
                                {{ trans('pages.editprofile.paint_photo') }}
                            </ui5-label>
                            <img
                                name="lakfotoimg"
                                id="lakfotoimg"
                                :src="profile.cloudFrontLakPath"
                                @error="
                                    imageSetTitle(
                                        $event,
                                        trans(
                                            'pages.editprofile.no_paint_photo'
                                        )
                                    )
                                "
                                @load="
                                    imageSetTitle(
                                        $event,
                                        trans('pages.editprofile.paint_photo')
                                    )
                                "
                            />
                            <ui5-button
                                class="lakfoto-upload-click"
                                @click="uploadClick('paint-image')"
                            >
                                {{
                                    trans(
                                        'pages.editprofile.upload_new_paint_photo'
                                    )
                                }}
                            </ui5-button>
                        </div>
                        <div class="field">
                            <ui5-label for="technicalfiche">
                                {{ trans('pages.editprofile.technical_sheet') }}
                            </ui5-label>
                            <ui5-button
                                id="technicalfiche"
                                class="fiche-upload-click"
                                @click="uploadClick('technical-fiche')"
                            >
                                {{
                                    trans('pages.editprofile.upload_new_sheet')
                                }}
                            </ui5-button>
                            <ui5-button
                                v-if="profile.technicalFichePath"
                                style="color: blue"
                                @click="technicalFicheClick"
                            >
                                {{ trans('pages.editprofile.download_sheet') }}
                            </ui5-button>
                            <ui5-button
                                v-else
                                :disabled="true"
                            >
                                {{ trans('pages.editprofile.no_sheet_found') }}
                            </ui5-button>
                        </div>
                    </template>
                    <div
                        class="field"
                        v-if="profileGroups"
                    >
                        <ui5-label
                            for="profilegroup"
                            required
                        >
                            {{ trans('pages.editprofile.group') }}
                        </ui5-label>
                        <ui5-select
                            id="profilegroup"
                            @change="
                                profile.groupRef =
                                    $event.detail.selectedOption.value
                            "
                            required
                        >
                            <option value=""></option>
                            <ui5-option
                                v-for="group in profileGroups"
                                :value="group.profileGroupId"
                                :selected="
                                    profile.groupRef == group.profileGroupId
                                "
                                :key="'profileGroup.' + group.profileGroupId"
                            >
                                {{ group.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.groupRef)"
                        >
                            {{ getErrorFromValidator($v.profile.groupRef) }}
                        </div>
                    </div>
                    <div
                        class="field"
                        v-if="profileSubgroups"
                    >
                        <ui5-label
                            for="profilesubgroup"
                            required
                        >
                            {{ trans('pages.editprofile.subgroup') }}
                        </ui5-label>
                        <ui5-select
                            id="profilesubgroup"
                            @change="
                                profile.subgroupRef =
                                    $event.detail.selectedOption.value
                            "
                            required
                        >
                            <ui5-option value=""></ui5-option>
                            <ui5-option
                                v-for="group in profileSubgroups"
                                :value="group.profileGroupId"
                                :selected="
                                    profile.subgroupRef == group.profileGroupId
                                "
                                :key="'profileSubgroup.' + group.profileGroupId"
                            >
                                {{ group.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.subgroupRef)"
                        >
                            {{ getErrorFromValidator($v.profile.subgroupRef) }}
                        </div>
                    </div>
                    <div
                        class="field"
                        v-if="stockGroups"
                    >
                        <ui5-label
                            for="profilestockgroup"
                            required
                        >
                            {{ trans('pages.editprofile.stock_group') }}
                        </ui5-label>
                        <ui5-select
                            id="profilestockgroup"
                            @change="
                                profile.stockgroupRef =
                                    $event.detail.selectedOption.value
                            "
                            required
                        >
                            <ui5-option value=""></ui5-option>
                            <ui5-option
                                v-for="group in stockGroups"
                                :value="group.profileGroupId"
                                :selected="
                                    profile.stockgroupRef ==
                                    group.profileGroupId
                                "
                                :key="'stockGroup.' + group.profileGroupId"
                            >
                                {{ group.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.stockgroupRef)
                            "
                        >
                            {{
                                getErrorFromValidator($v.profile.stockgroupRef)
                            }}
                        </div>
                    </div>
                    <div
                        class="field"
                        v-if="suppliers"
                    >
                        <ui5-label
                            for="profilesupplier"
                            required
                        >
                            {{ trans('pages.editprofile.supplier') }}
                        </ui5-label>
                        <ui5-select
                            id="profilesupplier"
                            @change="
                                profile.supplierRef =
                                    $event.detail.selectedOption.value
                            "
                            required
                        >
                            <ui5-option value=""></ui5-option>
                            <ui5-option
                                v-for="supplier in suppliers"
                                :value="supplier.id"
                                :selected="profile.supplierRef == supplier.id"
                                :key="'supplier.' + supplier.id"
                            >
                                {{ supplier.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.supplierRef)"
                        >
                            {{ getErrorFromValidator($v.profile.supplierRef) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="suppliercode"
                            required
                        >
                            {{
                                trans(
                                    'pages.editprofile.manufacturer_item_code'
                                )
                            }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="suppliercode"
                            id="suppliercode"
                            @input="profile.supplierCode = $event.target.value"
                            :value="profile.supplierCode"
                            required
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.supplierCode)
                            "
                        >
                            {{ getErrorFromValidator($v.profile.supplierCode) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="supplierProfileCode"
                            required
                        >
                            {{ trans('pages.editprofile.supplier_item_code') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="supplierProfileCode"
                            id="supplierProfileCode"
                            :value="profile.supplierProfileCode"
                            @input="
                                profile.supplierProfileCode =
                                    $event.target.value
                            "
                            required
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator(
                                    $v.profile.supplierProfileCode
                                )
                            "
                        >
                            {{
                                getErrorFromValidator(
                                    $v.profile.supplierProfileCode
                                )
                            }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="goods-type-id">
                            {{ trans('pages.editprofile.goods_type') }}
                        </ui5-label>
                        <ui5-select
                            v-if="goodsTypeOptions"
                            @change="
                                profile.goodsTypeId = $event.detail
                                    .selectedOption.value
                                    ? parseInt(
                                          $event.detail.selectedOption.value
                                      )
                                    : null
                            "
                        >
                            <ui5-option
                                v-for="option in goodsTypeOptions"
                                :selected="option.value == profile.goodsTypeId"
                                :key="'goodsTypeOption.' + option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.goodsTypeId)"
                        >
                            {{ getErrorFromValidator($v.profile.goodsTypeId) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="barcode"
                            required
                        >
                            {{ trans('pages.editprofile.barcode') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="barcode"
                            id="barcode"
                            :value="profile.barcode"
                            @input="profile.barcode = $event.target.value"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.barcode)"
                        >
                            {{ getErrorFromValidator($v.profile.barcode) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="info">
                            {{ trans('pages.editprofile.extra_info') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="info"
                            id="info"
                            :value="profile.info"
                            @input="profile.info = $event.target.value"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.info)"
                        >
                            {{ getErrorFromValidator($v.profile.info) }}
                        </div>
                    </div>
                    <h2>
                        {{ trans('pages.editprofile.stocks_and_packaging') }}
                    </h2>
                    <div class="field">
                        <ui5-label for="weight">
                            {{ trans('pages.editprofile.weight') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="weight"
                            id="weight"
                            :value="profile ? profile.weight : 0"
                            @input="profile.weight = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.weight)"
                        >
                            {{ getErrorFromValidator($v.profile.weight) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="weightBox">
                            {{ trans('pages.editprofile.package_weight') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="weightBox"
                            id="weightBox"
                            :value="profile ? profile.weightBox : 0"
                            @input="profile.weightBox = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.weightBox)"
                        >
                            {{ getErrorFromValidator($v.profile.weightBox) }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofileprofileunit"
                        v-if="profileUnits"
                    >
                        <ui5-label
                            for="profileunit"
                            required
                        >
                            {{ trans('pages.editprofile.unit') }}
                        </ui5-label>
                        <ui5-select
                            id="profileunit"
                            @change="
                                profile.unitRef =
                                    $event.detail.selectedOption.value
                            "
                            required
                        >
                            <ui5-option value=""></ui5-option>
                            <ui5-option
                                v-for="unit in profileUnits"
                                :value="unit.profileUnitId"
                                :selected="
                                    profile.unitRef == unit.profileUnitId
                                "
                                :key="'profileUnit.' + unit.profileUnitId"
                            >
                                {{ unit.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.unitRef)"
                        >
                            {{ getErrorFromValidator($v.profile.unitRef) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="packagePerUnit"
                            required
                        >
                            {{ trans('pages.editprofile.package_per_unit') }}
                        </ui5-label>
                        <translations-edit
                            ref="translationsPackagePerUnit"
                            :get-error-from-validator="getErrorFromValidator"
                            :extra-validations="packagePerUnitValidations"
                            :translation-data="
                                profile.translations.packagePerUnit
                            "
                        ></translations-edit>
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.packagePerUnit)
                            "
                        ></div>
                    </div>
                    <div class="field">
                        <ui5-label for="countPerKg">
                            {{ trans('pages.editprofile.count_per_kg') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="countPerKg"
                            id="countPerKg"
                            :value="profile ? profile.countPerKg : 0"
                            @input="profile.countPerKg = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.countPerKg)"
                        >
                            {{ getErrorFromValidator($v.profile.countPerKg) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-checkbox
                            id="keepStock"
                            :text="trans('pages.editprofile.keep_stock')"
                            :checked="profile.keepStock == 1"
                            @change="
                                profile.keepStock = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.keepStock)"
                        >
                            {{ getErrorFromValidator($v.profile.keepStock) }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofilecountsequence"
                    >
                        <ui5-label for="countsequence">
                            {{ trans('pages.editprofile.count_sequence') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="countsequence"
                            id="countsequence"
                            :value="profile ? profile.countsequence : 0"
                            @input="profile.countsequence = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.countsequence)
                            "
                        >
                            {{
                                getErrorFromValidator($v.profile.countsequence)
                            }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="minStock"
                            required
                        >
                            {{ trans('pages.editprofile.min_stock') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="minStock"
                            id="minStock"
                            :value="profile ? profile.minStock : 0"
                            @input="profile.minStock = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.minStock)"
                        >
                            {{ getErrorFromValidator($v.profile.minStock) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="maxStock"
                            required
                        >
                            {{ trans('pages.editprofile.max_stock') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="maxStock"
                            id="maxStock"
                            :value="profile ? profile.maxStock : 0"
                            @input="profile.maxStock = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.maxStock)"
                        >
                            {{ getErrorFromValidator($v.profile.maxStock) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="currentstockcount"
                            required
                        >
                            {{ trans('pages.editprofile.stock') }}
                        </ui5-label>
                        <ui5-input
                            type="number"
                            name="currentstockcount"
                            id="currentstockcount"
                            :value="profile ? profile.currentstockcount : 0"
                            @input="
                                profile.currentstockcount = $event.target.value
                            "
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator(
                                    $v.profile.currentstockcount
                                )
                            "
                        >
                            {{
                                getErrorFromValidator(
                                    $v.profile.currentstockcount
                                )
                            }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="spareStock">
                            {{ trans('pages.editprofile.spare_stock') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="spareStock"
                            id="spareStock"
                            :value="profile ? profile.spareStock : 0"
                            @input="profile.spareStock = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.spareStock)"
                        >
                            {{ getErrorFromValidator($v.profile.spareStock) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="latestCount">
                            {{ trans('pages.editprofile.latest_count') }}
                        </ui5-label>
                        <ui5-date-picker
                            id="latestCount"
                            format-pattern="dd-MM-yyyy"
                            :value="profile.latestCount"
                            readonly
                        />
                    </div>
                    <div class="field">
                        <ui5-label
                            for="location"
                            required
                        >
                            {{ trans('pages.editprofile.rack') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="location"
                            id="location"
                            :value="profile.location"
                            @input="profile.location = $event.target.value"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.location)"
                        >
                            {{ getErrorFromValidator($v.profile.location) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="location2">
                            {{ trans('pages.editprofile.rack_2') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="location2"
                            id="location2"
                            :value="profile.location2"
                            @input="profile.location2 = $event.target.value"
                            value=""
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.location2)"
                        >
                            {{ getErrorFromValidator($v.profile.location2) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="binLocation"
                            required
                        >
                            {{ trans('pages.editprofile.bin_location') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="binLocation"
                            id="binLocation"
                            :value="profile.binLocation"
                            @input="profile.binLocation = $event.target.value"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.binLocation)"
                        >
                            {{ getErrorFromValidator($v.profile.binLocation) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="descriptionKanban">
                            {{ trans('pages.editprofile.order_quantity') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="descriptionKanban"
                            id="descriptionKanban"
                            :value="profile.descriptionKanban"
                            @input="
                                profile.descriptionKanban = $event.target.value
                            "
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator(
                                    $v.profile.descriptionKanban
                                )
                            "
                        >
                            {{
                                getErrorFromValidator(
                                    $v.profile.descriptionKanban
                                )
                            }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="deliveryPeriod">
                            {{ trans('pages.editprofile.delivery_period') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            name="deliveryPeriod"
                            id="deliveryPeriod"
                            :value="profile.deliveryPeriod"
                            @input="
                                profile.deliveryPeriod = $event.target.value
                            "
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.deliveryPeriod)
                            "
                        >
                            {{
                                getErrorFromValidator($v.profile.deliveryPeriod)
                            }}
                        </div>
                    </div>

                    <template v-if="hasRight('administration')">
                        <h2>{{ trans('pages.editprofile.prices') }}</h2>
                        <div class="field">
                            <ui5-checkbox
                                id="pricelist"
                                :text="trans('pages.editprofile.price_list')"
                                :checked="profile.pricelist == 1"
                                @change="
                                    profile.pricelist = $event.target.checked
                                        ? '1'
                                        : '0'
                                "
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator($v.profile.pricelist)
                                "
                            >
                                {{
                                    getErrorFromValidator($v.profile.pricelist)
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="latestPriceControl">
                                {{
                                    trans(
                                        'pages.editprofile.latest_price_control'
                                    )
                                }}
                            </ui5-label>
                            <ui5-date-picker
                                id="latestPriceControl"
                                format-pattern="dd-MM-yyyy"
                                :value="profile.latestPriceControl"
                                @change="
                                    profile.latestPriceControl =
                                        $event.detail.value
                                "
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.latestPriceControl
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.latestPriceControl
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="eosolPrice">
                                {{ trans('pages.editprofile.eosol_vp') }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="eosolPrice"
                                id="eosolPrice"
                                v-model="profile.eosolPrice"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator($v.profile.eosolPrice)
                                "
                            >
                                {{
                                    getErrorFromValidator($v.profile.eosolPrice)
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="EosolPriceLM">
                                {{
                                    trans(
                                        'pages.editprofile.eosol_price_per_meter'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="EosolPriceLM"
                                id="EosolPriceLM"
                                v-model="profile.EosolPriceLM"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.EosolPriceLM
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.EosolPriceLM
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="EosolPriceLMCoating">
                                {{
                                    trans(
                                        'pages.editprofile.eosol_price_per_meter_coating'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="EosolPriceLMCoating"
                                id="EosolPriceLMCoating"
                                v-model="profile.EosolPriceLMCoating"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.EosolPriceLMCoating
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.EosolPriceLMCoating
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="EosolBuyPriceLM">
                                {{
                                    trans(
                                        'pages.editprofile.eosol_buy_price_per_meter'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="EosolBuyPriceLM"
                                id="EosolBuyPriceLM"
                                v-model="profile.EosolBuyPriceLM"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.EosolBuyPriceLM
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.EosolBuyPriceLM
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="EosolBuyPriceLMCoating">
                                {{
                                    trans(
                                        'pages.editprofile.eosol_buy_price_per_meter_coating'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="EosolBuyPriceLMCoating"
                                id="EosolBuyPriceLMCoating"
                                v-model="profile.EosolBuyPriceLMCoating"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.EosolBuyPriceLMCoating
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.EosolBuyPriceLMCoating
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="price4">
                                {{ trans('pages.editprofile.price4_netto') }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="price4"
                                id="price4"
                                :value="profile ? profile.price4 : 0"
                                @input="profile.price4 = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="getErrorFromValidator($v.profile.price4)"
                            >
                                {{ getErrorFromValidator($v.profile.price4) }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="mmPrice">
                                {{ trans('pages.editprofile.mm_vp') }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="mmPrice"
                                id="mmPrice"
                                :value="profile ? profile.mmPrice : 0"
                                @input="profile.mmPrice = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="getErrorFromValidator($v.profile.mmPrice)"
                            >
                                {{ getErrorFromValidator($v.profile.mmPrice) }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="mmPriceLM">
                                {{
                                    trans(
                                        'pages.editprofile.mm_price_per_meter'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="mmPriceLM"
                                id="mmPriceLM"
                                :value="profile ? profile.mmPriceLM : 0"
                                @input="profile.mmPriceLM = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator($v.profile.mmPriceLM)
                                "
                            >
                                {{
                                    getErrorFromValidator($v.profile.mmPriceLM)
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="mmPriceLMCoating">
                                {{
                                    trans(
                                        'pages.editprofile.mm_price_per_meter_coating'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="mmPriceLMCoating"
                                id="mmPriceLMCoating"
                                :value="profile ? profile.mmPriceLMCoating : 0"
                                @input="profile.mmPriceLMCoating = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.mmPriceLMCoating
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.mmPriceLMCoating
                                    )
                                }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="bruto">
                                {{
                                    trans(
                                        'pages.editprofile.gross_purchase_price'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="bruto"
                                id="bruto"
                                :value="profile ? profile.bruto : 0"
                                @input="profile.bruto = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="getErrorFromValidator($v.profile.bruto)"
                            >
                                {{ getErrorFromValidator($v.profile.bruto) }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="netto">
                                {{
                                    trans(
                                        'pages.editprofile.net_purchase_price'
                                    )
                                }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="netto"
                                id="netto"
                                :value="profile ? profile.netto : 0"
                                @input="profile.netto = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="getErrorFromValidator($v.profile.netto)"
                            >
                                {{ getErrorFromValidator($v.profile.netto) }}
                            </div>
                        </div>
                        <div class="field">
                            <ui5-label for="eosolBuyPrice">
                                {{ trans('pages.editprofile.eosol_ap') }}
                            </ui5-label>
                            <text-input
                                type="number"
                                name="eosolBuyPrice"
                                id="eosolBuyPrice"
                                :value="profile ? profile.eosolBuyPrice : 0"
                                @input="profile.eosolBuyPrice = $event"
                                step="any"
                            />
                            <div
                                class="error"
                                v-if="
                                    getErrorFromValidator(
                                        $v.profile.eosolBuyPrice
                                    )
                                "
                            >
                                {{
                                    getErrorFromValidator(
                                        $v.profile.eosolBuyPrice
                                    )
                                }}
                            </div>
                        </div>
                    </template>
                    <h2>{{ trans('pages.editprofile.technical_data') }}</h2>
                    <div class="field">
                        <ui5-label
                            for="length"
                            required
                        >
                            {{ trans('pages.editprofile.profile_length') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="length"
                            id="length"
                            :value="profile ? profile.length : 0"
                            @input="profile.length = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.length)"
                        >
                            {{ getErrorFromValidator($v.profile.length) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="width"
                            required
                        >
                            {{ trans('pages.editprofile.profile_width') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="width"
                            id="width"
                            :value="profile ? profile.width : 0"
                            @input="profile.width = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.width)"
                        >
                            {{ getErrorFromValidator($v.profile.width) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="height"
                            required
                        >
                            {{ trans('pages.editprofile.profile_height') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="height"
                            id="height"
                            :value="profile ? profile.height : 0"
                            @input="profile.height = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.height)"
                        >
                            {{ getErrorFromValidator($v.profile.height) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="contour"
                            required
                        >
                            {{ trans('pages.editprofile.rotation') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="contour"
                            id="contour"
                            :value="profile ? profile.contour : 0"
                            @input="profile.contour = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.contour)"
                        >
                            {{ getErrorFromValidator($v.profile.contour) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="contour2"
                            required
                        >
                            {{ trans('pages.editprofile.rotation2') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="contour2"
                            id="contour2"
                            :value="profile ? profile.contour2 : 0"
                            @input="profile.contour2 = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.contour2)"
                        >
                            {{ getErrorFromValidator($v.profile.contour2) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="weightLM"
                            required
                        >
                            {{ trans('pages.editprofile.weight_lm') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="weightLM"
                            id="weightLM"
                            :value="profile ? profile.weightLM : 0"
                            @input="profile.weightLM = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.contour2)"
                        >
                            {{ getErrorFromValidator($v.profile.contour2) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label
                            for="roundToLength"
                            required
                        >
                            {{ trans('pages.editprofile.round_to_mm') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="roundToLength"
                            id="roundToLength"
                            :value="profile ? profile.roundToLength : 0"
                            @input="profile.roundToLength = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.roundToLength)
                            "
                        >
                            {{
                                getErrorFromValidator($v.profile.roundToLength)
                            }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofileminlengthtoround"
                    >
                        <ui5-label
                            for="minLengthToRound"
                            required
                        >
                            {{ trans('pages.editprofile.round_from_mm') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="minLengthToRound"
                            id="minLengthToRound"
                            :value="profile ? profile.minLengthToRound : 0"
                            @input="profile.minLengthToRound = $event"
                            step="any"
                            required
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator(
                                    $v.profile.minLengthToRound
                                )
                            "
                        >
                            {{
                                getErrorFromValidator(
                                    $v.profile.minLengthToRound
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofileispart"
                    >
                        <ui5-checkbox
                            id="isPart"
                            :text="trans('pages.editprofile.process_as_part')"
                            :checked="profile.isPart == 1"
                            @change="
                                profile.isPart = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.isPart)"
                        >
                            {{ getErrorFromValidator($v.profile.isPart) }}
                        </div>
                    </div>

                    <h2>{{ trans('pages.editprofile.platform') }}</h2>
                    <div
                        class="field"
                        id="editprofileactive"
                    >
                        <ui5-checkbox
                            id="active"
                            :text="trans('pages.editprofile.active')"
                            :checked="profile.active == 1"
                            @change="
                                profile.active = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.active)"
                        >
                            {{ getErrorFromValidator($v.profile.active) }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofileprosite"
                    >
                        <ui5-checkbox
                            id="prosite"
                            :text="trans('pages.editprofile.prosite_mm')"
                            :checked="profile.prosite == 1"
                            @change="
                                profile.prosite = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.prosite)"
                        >
                            {{ getErrorFromValidator($v.profile.prosite) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-checkbox
                            id="prositeEosol"
                            :text="trans('pages.editprofile.prosite_eosol')"
                            :checked="profile.prositeEosol == 1"
                            @change="
                                profile.prositeEosol = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator($v.profile.prositeEosol)
                            "
                        >
                            {{ getErrorFromValidator($v.profile.prositeEosol) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-checkbox
                            id="prositeEosol"
                            :text="trans('pages.editprofile.active_webshop')"
                            :checked="profile.webshop == 1"
                            @change="
                                profile.webshop = $event.target.checked
                                    ? '1'
                                    : '0'
                            "
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.webshop)"
                        >
                            {{ getErrorFromValidator($v.profile.webshop) }}
                        </div>
                    </div>
                    <div class="field">
                        <ui5-label for="sortValue">
                            {{ trans('pages.editprofile.sort_value') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="sortValue"
                            id="sortValue"
                            :value="profile ? profile.sortValue : 0"
                            @input="profile.sortValue = $event"
                            step="any"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.sortValue)"
                        >
                            {{ getErrorFromValidator($v.profile.sortValue) }}
                        </div>
                    </div>
                    <h2>
                        {{ trans('pages.editprofile.material_information') }}
                    </h2>
                    <div
                        class="field"
                        id="editprofileactive"
                    >
                        <ui5-label for="materialTypeId">
                            {{ trans('pages.editprofile.material_type') }}
                        </ui5-label>
                        <ui5-select
                            name="materialTypeId"
                            id="materialTypeId"
                            :value="profile ? profile.material_type_id : null"
                            @change="
                                profile.material_type_id = $event.target.value
                            "
                        >
                            <ui5-option value="">
                                {{
                                    trans('pages.editprofile.no_material_type')
                                }}
                            </ui5-option>
                            <ui5-option
                                v-for="materialType in materialTypes"
                                :key="`material-type-${materialType.id}`"
                                :value="materialType.id"
                                :selected="
                                    profile.material_type_id == materialType.id
                                "
                            >
                                {{ materialType.name }}
                            </ui5-option>
                        </ui5-select>
                        <div
                            class="error"
                            v-if="
                                getErrorFromValidator(
                                    $v.profile.material_type_id
                                )
                            "
                        >
                            {{
                                getErrorFromValidator(
                                    $v.profile.material_type_id
                                )
                            }}
                        </div>
                    </div>
                    <div
                        class="field"
                        id="editprofileactive"
                    >
                        <ui5-label for="thickness">
                            {{ trans('pages.editprofile.thickness') }}
                        </ui5-label>
                        <text-input
                            type="number"
                            name="thickness"
                            id="thickness"
                            :value="profile ? profile.thickness : 0"
                            @input="profile.thickness = $event"
                            step="0.01"
                        />
                        <div
                            class="error"
                            v-if="getErrorFromValidator($v.profile.thickness)"
                        >
                            {{ getErrorFromValidator($v.profile.thickness) }}
                        </div>
                    </div>
                </form>
            </div>
        </template>
    </div>
</template>
<script>
import profileApi from '../api/profile';
import supplierApi from '../api/supplier';
import { required, requiredIf } from 'vuelidate/lib/validators';
import SpinnerText from '../components/loader/SpinnerText';
import ErrorHelper from '../helpers/ErrorHelper';
import dropzoneTranslations from '../shared/dropzoneTranslations';
import DropZone from '../components/DropZone';
import prefixApi from '../api/prefix';
import { stripNonNumeric } from '../helpers/StringHelpers';
import Spinner from '../components/loader/Spinner';
import TranslationsEdit from '../components/TranslationsEdit';
import languageApi from '../api/language';
import IsDebugMixin from '../mixins/is-debug';
import goodsTypeApi from '../api/goodsType';
import materialTypesApi from '../api/materialTypes';
import { mapGetters } from 'vuex';
import LocalizationHelper from '../helpers/LocalizationHelper';
import { showErrorMessage } from '../dialogs';
import _ from 'lodash';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput.vue';

const imageFileTypes = [
    'image/gif',
    'image/jpeg',
    'image/png',
    'capture=camera',
];
export default {
    components: { Spinner, DropZone, SpinnerText, TranslationsEdit, TextInput },
    validations() {
        return {
            profile: {
                code: { required },
                groupRef: { required },
                subgroupRef: { required },
                stockgroupRef: { required },
                supplierRef: { required },
                supplierCode: { required },
                supplierProfileCode: { required },
                barcode: { required },
                description: { required },
                descriptionFR: {},
                info: {},
                weightBox: {},
                weight: {},
                countPerKg: {},
                minStock: { required },
                maxStock: { required },
                currentstockcount: { required },
                unitRef: { required },
                keepStock: { required },
                countsequence: {},
                location: { required },
                eosolPrice: {},
                EosolPriceLM: {},
                EosolPriceLMCoating: {},
                EosolBuyPriceLM: {},
                EosolBuyPriceLMCoating: {},
                price4: {},
                mmPrice: {},
                mmPriceLMCoating: {},
                bruto: {},
                netto: {},
                eosolBuyPrice: {},
                mmPriceLM: {},
                latestPriceControl: {},
                pricelist: { required },
                length: { required },
                width: { required },
                height: { required },
                contour: { required },
                contour2: { required },
                roundToLength: { required },
                minLengthToRound: { required },
                location2: {},
                binLocation: { required },
                descriptionKanban: {},
                deliveryPeriod: {},
                spareStock: {},
                isPart: { required },
                active: { required },
                prosite: { required },
                prositeEosol: { required },
                webshop: { required },
                sortValue: {},
                material_type_id: {},
                thickness: {
                    required: requiredIf(function (profile) {
                        return profile.material_type_id != null;
                    }),
                },
            },
        };
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        goodsTypeOptions() {
            if (!this.goodsTypes) {
                return null;
            }
            return [
                { value: null, label: '---' },
                ...this.goodsTypes.map((x) => {
                    return { value: x.id, label: x.label };
                }),
            ];
        },
        packagePerUnitValidations() {
            return { required };
        },
        levelOneOptions() {
            if (!this.prefixes) {
                return [];
            }
            return this.prefixes.filter((x) => x.level == 1);
        },
        levelTwoOptions() {
            const empty = {
                ID: 'empty2',
                label: '',
                value: '',
                level: 2,
                parent: null,
            };
            if (!this.levelOneOptions) {
                return [empty];
            }

            const selectedId1 = this.selectedPrefixes[0];
            if (!selectedId1) {
                return [empty];
            }

            const items = this.prefixes.filter(
                (x) =>
                    x.level == 2 &&
                    (x.parent == selectedId1 || x.parent == null)
            );
            items.sort((a, b) => {
                return (a.parent == null ? 1 : 0) - (b.parent == null ? 1 : 0);
            });
            return [empty, ...items];
        },
        levelThreeOptions() {
            const empty = {
                ID: 'empty3',
                label: '',
                value: '',
                level: 3,
                parent: null,
            };

            if (!this.levelOneOptions) {
                return [empty];
            }

            const selectedId2 = this.selectedPrefixes[1];
            if (!selectedId2) {
                return [empty];
            }

            const items = this.prefixes.filter(
                (x) =>
                    x.level == 3 &&
                    (x.parent == selectedId2 || x.parent == null)
            );
            items.sort((a, b) => {
                return (a.parent == null ? 1 : 0) - (b.parent == null ? 1 : 0);
            });
            return [empty, ...items];
        },
        imageUrl() {
            return (
                this.baseUrl +
                '/profile/fetchImage/?profileId=' +
                this.profile.profileId
            );
        },
        dropOptions() {
            const sessionKey = this.$store.getters['currentUser/getSessionKey'];

            return {
                url: Config.url + '/profile/uploadImage',
                chunking: false,
                thumbnailWidth: 150, // px
                thumbnailHeight: 150,
                addRemoveLinks: true,
                autoProcessQueue: true,
                createImageThumbnails: true,
                acceptedFiles: imageFileTypes.join(','),
                paramName: 'image_data',
                headers: {
                    'X-App-Version': Config.version,
                    Authorization: sessionKey
                        ? 'Bearer ' + sessionKey
                        : undefined,
                },
                ...dropzoneTranslations,
                dictDefaultMessage: `${this.trans(
                    'pages.editprofile.add_images'
                )} <i class="fa fa-upload"></i> <i class="fa fa-camera"></i>`,
            };
        },
        id() {
            return this.$route.params.id;
        },
        baseUrl() {
            return Config.url;
        },
        profileCode() {
            return this.profile ? this.profile.code : null;
        },
        copy() {
            return this.$route.query.copy;
        },
    },
    mixins: [IsDebugMixin, localization],
    data() {
        return {
            fetchingProfiles: false,
            calculateArticleNumber: null,
            selectedPrefixes: {
                0: null,
                1: null,
                2: null,
            },
            uploadType: null,
            profile: null,
            profileGroups: null,
            profileSubgroups: null,
            stockGroups: null,
            suppliers: null,
            profileUnits: null,
            loading: false,
            uploading: false,
            imageFiles: [],
            imageFileTypes,
            prefixes: null,
            languages: null,
            codeExists: false,
            goodsTypes: null,
            materialTypes: null,
        };
    },
    watch: {
        profileCode() {
            this.setTitle();
        },
        id() {
            this.load();
        },
        copy() {
            this.load();
        },
        selectedPrefixes: {
            deep: true,
            handler() {
                this.calculateArticleNumber();
            },
        },
    },
    mounted() {
        this.calculateArticleNumber = _.debounce(
            this.calculateArticleNumberInner,
            300
        );
        this.load();
        prefixApi.fetchAllPrefixes().then((x) => {
            this.prefixes = x.data.data;
            this.fillPrefixSelection();
        });
    },
    methods: {
        profileCodeChanged($event) {
            const field = $event.target;
            const code = this.profile.code;
            field.disabled = true;
            profileApi
                .codeExists(code, this.id)
                .then((response) => {
                    this.codeExists = response.data.data.exists;
                    if (this.codeExists) {
                        showErrorMessage(
                            this.trans(
                                'pages.editprofile.errors.article_code_in_use'
                            )
                        );
                    }
                    field.disabled = false;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.editprofile.errors.article_number_lookup_error'
                        )
                    );
                });
        },
        isValid() {
            let valid = true;
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x.$v && x.$v.$invalid) {
                    valid = false;
                }
            });
            return valid && !this.codeExists;
        },
        calculateArticleNumberInner() {
            if (!this.profile || this.profile.profileId) {
                return;
            }

            if (
                !this.selectedPrefixes[0] ||
                !this.selectedPrefixes[1] ||
                !this.selectedPrefixes[2]
            ) {
                this.profile.code = '';
            } else {
                const prefix1 = this.getPrefixValueById(this.selectedPrefixes[0]);
                const prefix2 = this.getPrefixValueById(this.selectedPrefixes[1]);
                const prefix3 = this.getPrefixValueById(this.selectedPrefixes[2]);

                this.profile.code = '';
                const start = prefix1 + prefix2 + prefix3;
                this.fetchingProfiles = true;
                profileApi
                    .fetchProfileCodesStartingWith(start)
                    .then((response) => {
                        const codes = response.data.data;
                        const numbers = codes.map((x) => {
                            const stripped = stripNonNumeric(
                                x.substr(start.length)
                            );
                            return parseInt(
                                stripped.substr(stripped.length - 3)
                            );
                        });
                        const usedNumbers = new Set(numbers);
                        let number = 0;
                        while (usedNumbers.has(number)) {
                            number++;
                        }
                        const startLength = start.length;
                        const targetLength = 7 - startLength;
                        this.profile.code =
                            start +
                            number.toString().padStart(targetLength, '0');
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.editprofile.errors.article_number_generation_error'
                            )
                        );
                    })
                    .finally(() => {
                        this.fetchingProfiles = false;
                    });
            }
        },
        getPrefixValueById(id) {
            if (!id || !this.prefixes) {
                return null;
            }
            if (id.toString().startsWith('empty')) {
                return '';
            }
            const prefix = this.prefixes.find((x) => x.ID == id);
            return prefix ? prefix.value : null;
        },
        fillPrefixSelection() {
            this.$nextTick(() => {
                if (
                    this.levelOneOptions[0] &&
                    shouldSetFirst(
                        this.levelOneOptions,
                        this.selectedPrefixes[0]
                    )
                ) {
                    this.selectedPrefixes[0] = this.levelOneOptions[0].ID;
                }

                this.$nextTick(() => {
                    if (
                        this.levelTwoOptions[0] &&
                        shouldSetFirst(
                            this.levelTwoOptions,
                            this.selectedPrefixes[1]
                        )
                    ) {
                        this.selectedPrefixes[1] = this.levelTwoOptions[0].ID;
                    }

                    this.$nextTick(() => {
                        if (
                            this.levelThreeOptions[0] &&
                            shouldSetFirst(
                                this.levelThreeOptions,
                                this.selectedPrefixes[2]
                            )
                        ) {
                            this.selectedPrefixes[2] =
                                this.levelThreeOptions[0].ID;
                        }
                    });
                });
            });

            function shouldSetFirst(options, prefix) {
                return (
                    !prefix ||
                    !(options.filter((x) => x.ID == prefix).length > 0)
                );
            }
        },
        fileRemoved(file) {
            if (file.ID) {
                profileApi.removeImageById(file.ID).catch(() => {
                    showErrorMessage(
                        this.trans('pages.editprofile.errors.file_delete_error')
                    );
                    file.removed = false;
                });
            }
        },
        mainImageSet(file) {
            if (file.ID) {
                profileApi.setMainImage(file.ID).catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.editprofile.errors.main_file_set_error'
                        )
                    );
                });
            }
        },
        uploadFiles(files) {
            const promises = files.map((file) => {
                file.uploading = true;
                file.progress = 0;

                return profileApi
                    .uploadImageNew(
                        file.fileObject,
                        this.profile.profileId,
                        file.isMain,
                        (progressEvent) => {
                            file.progress = Math.round(
                                (progressEvent.loaded * 100) /
                                    progressEvent.total
                            );
                        }
                    )
                    .then((response) => {
                        const data = response.data.data;
                        if (data.success == 0) {
                            showErrorMessage(data.msg);
                        }
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans('pages.editprofile.errors.upload_error')
                        );
                    });
            });

            Promise.all(promises).then(() => {
                this.loadImages(this.id);
            });
        },
        imageSetTitle($event, alt) {
            $event.target.alt = alt;
            $event.target.title = alt;
        },
        getEmoji: LocalizationHelper.languageCodeToEmoji,
        getErrorFromValidator:
            ErrorHelper.getErrorFromValidatorWithoutDirtyCheck,
        createEmptyProfile() {
            return {
                profileId: '',
                barcode: '',
                descriptionFR: '',
                description: '',
                info: '',
                code: '',
                length: '',
                width: '',
                height: '',
                weight: '',
                contour: '',
                contour2: '',
                weightLM: '',
                weightBox: '',
                countPerKg: '',
                translations: {
                    packagePerUnit: {},
                },
                supplierCode: '',
                supplierProfileCode: '',
                supplierId: '',
                roundToLength: '',
                minLengthToRound: '',
                minStock: '',
                maxStock: '',
                countsequence: '0',
                currentstockcount: '',
                latestCount: '',
                keepStock: '0',
                location: '',
                bruto: '',
                netto: '',
                mmPrice: '',
                eosolPrice: '',
                eosolBuyPrice: '',
                price4: '',
                latestPriceControl: '',
                pricelist: '0',
                active: '0',
                prosite: '0',
                prositeEosol: '0',
                webshop: '0',
                supplierRef: '',
                groupRef: '',
                subgroupRef: '',
                stockgroupRef: '',
                unitRef: '',
                location2: '',
                binLocation: '',
                descriptionKanban: '',
                deliveryPeriod: '',
                spareStock: '0',
                technicalFichePath: null,
                lakPhotoPath: null,
                isPart: '0',
                sortValue: '0',
                mmPriceLM: '0',
                EosolPriceLM: '0',
                EosolBuyPriceLM: '0',
                mmPriceLMCoating: '0',
                EosolPriceLMCoating: '0',
                EosolBuyPriceLMCoating: '0',
            };
        },
        loadImages(profileId) {
            profileApi.fetchImageList(profileId).then((response) => {
                this.imageFiles.length = 0;
                response.data.data.forEach((image) => {
                    this.imageFiles.push({
                        ID: image.ID,
                        fileObject: null,
                        previewSrc: image.cloudFrontPath,
                        newFile: false,
                        removed: false,
                        progress: 0,
                        isMain: image.mainImage == 1,
                    });
                });
            });
        },
        load() {
            this.loading = true;

            const promises = [];
            if (this.id) {
                promises.push(this.loadProfile(this.id));
                promises.push(this.loadImages(this.id));
            } else if (this.copy) {
                promises.push(this.populateCopy(this.copy));
            } else {
                this.profile = this.createEmptyProfile();
            }

            promises.push(
                languageApi.fetchAll().then((response) => {
                    this.languages = response.data.data;
                })
            );

            promises.push(
                profileApi.fetchAllProfileGroups().then((response) => {
                    this.profileGroups = response.data.data;
                })
            );

            promises.push(
                profileApi.fetchAllProfileSubGroups().then((response) => {
                    this.profileSubgroups = response.data.data;
                })
            );

            promises.push(
                profileApi.fetchAllStockGroups().then((response) => {
                    this.stockGroups = response.data.data;
                })
            );

            promises.push(
                supplierApi.fetchAllSuppliers().then((response) => {
                    this.suppliers = response.data.data;
                })
            );

            promises.push(
                profileApi.fetchUnits().then((response) => {
                    this.profileUnits = response.data.data;
                })
            );

            promises.push(
                goodsTypeApi.fetchAll().then((response) => {
                    this.goodsTypes = response.data.data;
                })
            );

            promises.push(
                materialTypesApi.fetchAll().then((response) => {
                    if (!response.data || !response.data.data) {
                        this.materialTypes = [];
                        return;
                    }

                    this.materialTypes = response.data.data;
                })
            );

            Promise.all(promises).then(() => {
                this.loading = false;
                if (!this.id) {
                    this.addEmptyTranslations();
                }
            });
        },
        addEmptyTranslations() {
            const translations = {};
            this.languages.forEach((x) => {
                translations[x.code] = '';
            });
            this.profile.translations.packagePerUnit = translations;
        },
        setTitle() {
            if (this.profile && this.profile.profileId) {
                this.$store.commit(
                    'general/setPageTitle',
                    'Profiel ' + this.profile.code,
                    { root: true }
                );
            }
        },
        createDateInitialValue(timestamp) {
            if (timestamp <= 0) {
                return '';
            }
            const date = new Date(timestamp * 1000);
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const days = date.getDate().toString().padStart(2, '0');
            return days + '-' + month + '-' + year;
        },
        saveEditProfile() {
            if (!this.isValid()) {
                return showErrorMessage(
                    this.trans('pages.editprofile.errors.input_errors')
                );
            }

            const files = {};
            const infos = {};
            this.imageFiles.forEach((file, index) => {
                const identifier = 'file_' + index;
                if (file.fileObject) {
                    files[identifier] = file.fileObject;
                }
                if (file.fileObject || !file.removed) {
                    infos[identifier] = {
                        ID: file.ID,
                        isMain: file.isMain,
                        removed: file.removed,
                    };
                }
            });

            const payload = {
                profileId: this.id ? this.id : null,
                code: this.profile.code,
                barcode: this.profile.barcode,
                groupRef: this.profile.groupRef,
                subgroupRef: this.profile.subgroupRef,
                stockgroupRef: this.profile.stockgroupRef,
                unitRef: this.profile.unitRef,
                supplierRef: this.profile.supplierRef,
                description: this.profile.description,
                descriptionFR: this.profile.descriptionFR,
                info: this.profile.info,
                supplierCode: this.profile.supplierCode,
                supplierProfileCode: this.profile.supplierProfileCode,
                minStock: this.profile.minStock,
                maxStock: this.profile.maxStock,
                currentstockcount: this.profile.currentstockcount,
                packagePerUnit: { ...this.profile.translations.packagePerUnit },
                keepStock: this.profile.keepStock,
                countsequence: this.profile.countsequence,
                location: this.profile.location,
                bruto:
                    this.profile.bruto != '' ? this.profile.bruto : undefined,
                netto:
                    this.profile.netto != '' ? this.profile.netto : undefined,
                mmPrice:
                    this.profile.mmPrice != ''
                        ? this.profile.mmPrice
                        : undefined,
                eosolPrice:
                    this.profile.eosolPrice != ''
                        ? this.profile.eosolPrice
                        : undefined,
                eosolBuyPrice:
                    this.profile.eosolBuyPrice != ''
                        ? this.profile.eosolBuyPrice
                        : undefined,
                price4:
                    this.profile.price4 != '' ? this.profile.price4 : undefined,
                mmPriceLM: this.profile.mmPriceLM || undefined,
                EosolPriceLM: this.profile.EosolPriceLM || undefined,
                EosolBuyPriceLM: this.profile.EosolBuyPriceLM || undefined,
                mmPriceLMCoating: this.profile.mmPriceLMCoating || undefined,
                EosolPriceLMCoating:
                    this.profile.EosolPriceLMCoating || undefined,
                EosolBuyPriceLMCoating:
                    this.profile.EosolBuyPriceLMCoating || undefined,
                weightBox: this.profile.weightBox,
                countPerKg: this.profile.countPerKg,
                latestPriceControl: this.profile.latestPriceControl,
                pricelist: this.profile.pricelist,
                prosite: this.profile.prosite,
                weight: this.profile.weight,
                length: this.profile.length,
                height: this.profile.height,
                width: this.profile.width,
                contour: this.profile.contour,
                contour2: this.profile.contour2,
                weightLM: this.profile.weightLM,
                roundToLength:
                    this.profile.roundToLength != ''
                        ? this.profile.roundToLength
                        : undefined,
                minLengthToRound:
                    this.profile.minLengthToRound != ''
                        ? this.profile.minLengthToRound
                        : undefined,
                location2: this.profile.location2,
                binLocation: this.profile.binLocation,
                descriptionKanban: this.profile.descriptionKanban,
                deliveryPeriod: this.profile.deliveryPeriod,
                spareStock: this.profile.spareStock,
                isPart: this.profile.isPart,
                active: this.profile.active,
                prositeEosol: this.profile.prositeEosol,
                webshop: this.profile.webshop,
                sortValue: this.profile.sortValue,
                goodsTypeId: this.profile.goodsTypeId,
                imageFiles: files,
                imageFileInfos: infos,
                material_type_id: this.profile.material_type_id,
                thickness: this.profile.thickness,
            };

            profileApi
                .update(payload)
                .then(() => {
                    this.$router.go(-1);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.editprofile.errors.profile_save_error'
                        )
                    );
                });
        },
        uploadClick(type) {
            this.uploadType = type;
            this.$refs.inputFileUpload.click();
        },
        inputFileUploadChange(event) {
            if (!event.target.files || event.target.files.length == 0) {
                return;
            }

            const files = event.target.files;
            const file = files[0];

            if (!File || !FileReader || !FileList || !Blob) {
                return showErrorMessage(
                    this.trans('pages.editprofile.errors.device_unsupported')
                );
            }

            const fileReader = new FileReader();
            const fileFilter =
                this.uploadType == 'technical-fiche'
                    ? ['application/pdf']
                    : this.imageFileTypes;
            if (!fileFilter.includes(file.type)) {
                return showErrorMessage(
                    this.trans('pages.editprofile.errors.file_type_unsupported')
                );
            }

            this.uploading = true;
            let func = null;
            let then = null;
            if (
                this.uploadType == 'image' ||
                this.uploadType == 'paint-image'
            ) {
                func = profileApi.uploadImage;
                then = (response) => {
                    const data = response.data.data;
                    if (data.success == 0) {
                        return showErrorMessage(data.msg);
                    }
                    if (this.uploadType == 'paint-image') {
                        this.profile.cloudFrontLakPath = data.cloudFrontLakPath;
                    }
                };
            } else if (this.uploadType == 'technical-fiche') {
                func = profileApi.uploadFiche;
                then = (response) => {
                    const data = response.data.data;
                    if (data.success == 0) {
                        return showErrorMessage(data.msg);
                    }
                    this.profile.technicalFichePath = data.path;
                };
            }

            func(
                file,
                this.profile.profileId,
                this.uploadType == 'image' ? 'Image' : 'LakPhoto'
            )
                .then(then)
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.editprofile.errors.upload_error')
                    );
                })
                .finally(() => {
                    this.uploading = false;
                });
        },
        technicalFicheClick() {
            window.open(
                Config.url +
                    '/resources/profiles/' +
                    this.profile.technicalFichePath
            );
        },
        populateCopy(profileId) {
            return this.loadProfile(profileId).then(() => {
                this.profile.profileId = '';

                const split = this.profile.code.split('-');
                let version = split[split.length - 1];
                if (split.length >= 2 && !isNaN(version)) {
                    version = parseInt(version);
                    version++;
                    split.pop();
                    this.profile.code = split.join('') + '-' + version;
                } else {
                    this.profile.code = this.profile.code + '-2';
                }
            });
        },
        loadProfile(profileId) {
            return profileApi.fetch(profileId).then((response) => {
                const profile = response.data.data;
                profile.latestCount = this.createDateInitialValue(
                    profile.latestCount
                );
                profile.latestPriceControl = this.createDateInitialValue(
                    profile.latestPriceControl
                );
                this.profile = profile;
            });
        },
    },
};
</script>
